import { effect, inject, Injectable, Injector, signal } from '@angular/core';
import { handleRequestError } from '@softline/application';
import { AbortedRequestError, ApiDataAccess, Query, QueryBuilder } from '@softline/core';
import { DateRange } from '@softline/ui-core';
import { SalesStatisticsGridDataSource } from '../grid/sales-statistics-grid.data-source';
import { applyActiveFilters } from '../helpers/apply-active-filters.function';
import { RELOAD_DEBOUNCE_MS } from '../helpers/chart-theme';
import {
  addDateFilter,
  addDbPercentColumn,
  addPeriodColumns,
  addPeriodFilters,
  addValueColumns,
  splitByPeriod,
  valFromRow,
} from '../helpers/document-type-query.helpers';
import {
  INVENTORY_TOTAL_KEY,
  loadInventoryValues,
} from '../helpers/inventory-values';
import { ApiRow } from '../types/api-row';
import { TotalKpisData } from '../types/total-kpis-data';
import { SalesStatisticsLoadingService } from './sales-statistics-loading.service';
import { SalesStatisticsQueryService } from './sales-statistics-query.service';

interface LoadResult {
  data: ApiRow[];
}

@Injectable()
export class TotalKpisService {
  queryService = inject(SalesStatisticsQueryService);
  private dataAccess = inject(ApiDataAccess);
  private injector = inject(Injector);
  private loadingService = inject(SalesStatisticsLoadingService);

  private readonly _value = signal<TotalKpisData | null>(null);
  private readonly _isLoading = signal(false);
  private readonly _hasLoaded = signal(false);
  private readonly _error = signal<unknown | null>(null);
  private loadVersion = 0;
  private lastParamsKey = '';
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private abortController: AbortController | null = null;

  readonly value = this._value.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly hasLoaded = this._hasLoaded.asReadonly();
  readonly error = this._error.asReadonly();

  constructor() {
    effect(() => {
      const period = this.queryService.dateRange();
      const referencePeriod = this.queryService.referencePeriod();
      const activeFilters = this.queryService.activeFilters();
      const documentType = this.queryService.documentType();
      const statisticType = this.queryService.statisticType();
      const inventoryEnabled = this.queryService.inventoryEnabled();
      const inventoryDataTick = this.queryService.inventoryDataTick();

      const refreshTick = this.queryService.refreshTick();
      const paramsKey = JSON.stringify({
        period,
        referencePeriod,
        activeFilters,
        documentType,
        statisticType,
        // Only switching Lagerwert ON needs the extra aggregate; switching it
        // off just hides the tile.
        inventoryDataTick,
        refreshTick,
      });
      if (paramsKey === this.lastParamsKey) return;
      this.lastParamsKey = paramsKey;

      this._isLoading.set(true);
      this.loadingService.setLoading('totalKpis', true);

      if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null;
        this.load(period, referencePeriod, documentType, statisticType, inventoryEnabled);
      }, RELOAD_DEBOUNCE_MS);
    });
  }

  private async load(
    period: DateRange,
    referencePeriod: DateRange | null,
    documentType: string,
    statisticType: string,
    inventoryEnabled: boolean
  ): Promise<void> {
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;

    const version = ++this.loadVersion;
    this._error.set(null);
    this._isLoading.set(true);

    try {
      const ds = new SalesStatisticsGridDataSource(
        this.dataAccess,
        documentType,
        statisticType
      );

      // The Lagerwert total rides in its own narrow query (see
      // loadInventoryValues) so the toggle never re-reads net/cost/DB. Two rows
      // with a comparison period — it groups by query_period like the value query.
      const inventoryBase = new QueryBuilder<ApiRow>()
        .start(0)
        .end(referencePeriod ? 2 : 1);
      applyActiveFilters(inventoryBase, this.queryService);
      // Swallowed on purpose: a failed or aborted Lagerwert query must neither
      // surface as an unhandled rejection nor take the value query down — the
      // tile then simply has no Lagerwert.
      const inventoryPromise = inventoryEnabled
        ? loadInventoryValues(ds, inventoryBase, null, period, referencePeriod, {
            signal: controller.signal,
          }).catch(() => null)
        : null;

      let result: LoadResult;
      if (referencePeriod) {
        const builder = new QueryBuilder<ApiRow>().start(0).end(2);
        addPeriodColumns(builder);
        addValueColumns(builder);
        addDbPercentColumn(builder);
        applyActiveFilters(builder, this.queryService);
        addPeriodFilters(builder, period, referencePeriod);
        result = (await ds.loadAbortable(
          { query: builder.build() as Query<object> },
          controller.signal
        )) as LoadResult;
      } else {
        const builder = new QueryBuilder<ApiRow>().start(0).end(1);
        addValueColumns(builder);
        addDbPercentColumn(builder);
        applyActiveFilters(builder, this.queryService);
        addDateFilter(builder, period.from, period.to);
        result = (await ds.loadAbortable(
          { query: builder.build() as Query<object> },
          controller.signal
        )) as LoadResult;
      }

      const inventory = inventoryPromise
        ? ((await inventoryPromise)?.get(INVENTORY_TOTAL_KEY) ?? null)
        : null;

      if (version !== this.loadVersion) return;

      let o: ApiRow | undefined;
      let reference: ApiRow | undefined | null;

      if (referencePeriod) {
        const { current, previous } = splitByPeriod(result?.data ?? []);
        o = current[0];
        reference = previous[0];
      } else {
        o = result?.data?.[0] as ApiRow | undefined;
        reference = null;
      }

      // A period can hold stock without any invoices — don't throw the loaded
      // Lagerwert away just because the value rows are empty.
      if (!o && !reference && !inventory) {
        this._value.set(null);
        return;
      }

      const revenue = valFromRow(o, 'net');
      const wes = valFromRow(o, 'cost');
      const db = valFromRow(o, 'contribution_margin');
      const inventoryValue = inventory ? inventory.value : null;
      const hasReference = !!referencePeriod && !!reference;
      const refRevenue = hasReference ? valFromRow(reference, 'net') : null;
      const refWes = hasReference ? valFromRow(reference, 'cost') : null;
      const refDb = hasReference ? valFromRow(reference, 'contribution_margin') : null;
      const refInventoryValue =
        hasReference && inventory ? inventory.reference : null;

      this._value.set({
        revenue,
        wes,
        referenceRevenue: refRevenue,
        referenceWes: refWes,
        db,
        referenceDb: refDb,
        dbPercent: valFromRow(o, 'contribution_margin_percent'),
        referenceDbPercent: hasReference
          ? valFromRow(reference, 'contribution_margin_percent')
          : null,
        inventoryValue,
        referenceInventoryValue: refInventoryValue,
      });
    } catch (e) {
      if (e instanceof AbortedRequestError) return;
      if (version === this.loadVersion) {
        this._value.set(null);
        this._error.set(e);
      }
      handleRequestError(e, this.injector, { show: true });
    } finally {
      if (version === this.loadVersion) {
        this._isLoading.set(false);
        this._hasLoaded.set(true);
        this.loadingService.setLoading('totalKpis', false);
      }
      if (this.abortController === controller) this.abortController = null;
    }
  }
}
